import { useCallback, useState } from "react";
import type { AxiosError } from "axios";

import type { Lesson } from "../pages/LessonsPage/LessonsPage.types";
import { reorderLessons } from "../services/lessonService/lessonService";

type TableReorderPayload = {
  orderedIds: string[];
};

type ToastPayload = {
  severity: "success" | "info" | "warn" | "error";
  summary: string;
  detail: string;
  life?: number;
};

interface UseLessonOrderingOptions {
  courseId: number;
  lessons: Lesson[];
  setLessons: React.Dispatch<React.SetStateAction<Lesson[]>>;
  showToast: (payload: ToastPayload) => void;
}

interface UseLessonOrderingResult {
  handleReorder: (payload: TableReorderPayload) => Promise<void>;
  isSavingOrder: boolean;
}

export const useLessonOrdering = ({
  courseId,
  lessons,
  setLessons,
  showToast,
}: UseLessonOrderingOptions): UseLessonOrderingResult => {
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const handleReorder = useCallback(
    async ({ orderedIds }: TableReorderPayload) => {
      if (!orderedIds || orderedIds.length === 0 || isSavingOrder) {
        return;
      }

      const orderedLessonIds = orderedIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));

      if (orderedLessonIds.length !== lessons.length) {
        return;
      }

      const lessonById = new Map(lessons.map((lesson) => [lesson.id, lesson]));

      if (orderedLessonIds.some((lessonId) => !lessonById.has(lessonId))) {
        return;
      }

      const previousState = lessons;

      const nextLessons = orderedLessonIds.map((lessonId, index) => {
        const lesson = lessonById.get(lessonId)!;
        return {
          ...lesson,
          order: index + 1,
        };
      });

      setLessons(nextLessons);

      const payload = orderedLessonIds.reduce<Record<number, number>>((acc, lessonId, index) => {
        acc[lessonId] = index + 1;
        return acc;
      }, {});

      try {
        setIsSavingOrder(true);
        await reorderLessons(courseId, payload);
        showToast({ severity: "success", summary: "Порядок сохранён", detail: "Новый порядок уроков успешно сохранён", life: 2000 });
      } catch (error) {
        setLessons(previousState);

        const axiosError = error as AxiosError<{ detail?: string }>;
        const backendMessage = axiosError.response?.data?.detail;

        showToast({
          severity: "error",
          summary: "Не удалось сохранить порядок",
          detail: backendMessage || "Попробуйте ещё раз чуть позже",
          life: 4000,
        });
      } finally {
        setIsSavingOrder(false);
      }
    },
    [courseId, isSavingOrder, lessons, setLessons, showToast],
  );

  return {
    handleReorder,
    isSavingOrder,
  };
};

export default useLessonOrdering;

